import {
  Paper,
  TextCaption,
  TextLargeParagraph,
  TextPageHeader,
  TooltipBubble,
} from '@carboncopyco/ui';
import { integersFormatter } from '@carboncopyco/utils';
import {
  type AnalyticsKpiLineChartProps,
  EmptyState,
  KpiLineChartSkeleton,
  LineChart,
  type LineChartData,
} from '../Charts';
import { baseTooltipOptions, minimalChartOptions } from '../Charts/consts';
import { TrendLabel } from './TrendLabel';

export type AnalyticsCardProps = {
  chartColor?: AnalyticsKpiLineChartProps['color'];
  chart: LineChartData;
  title: string;
  breakdown: string;
  total: number | null;
  trend: number | null;
  isLoading: boolean;
};

export const AnalyticsCard = ({
  chartColor,
  chart,
  title,
  breakdown,
  total,
  trend,
  isLoading,
}: AnalyticsCardProps) => {
  const roundedTotal = total && integersFormatter(total);

  if (!roundedTotal && !isLoading) {
    return (
      <Paper
        border="ds-regular"
        fillParent
        radius
      >
        <EmptyState />
      </Paper>
    );
  }

  return (
    <Paper
      border="ds-regular"
      fillParent
      radius
    >
      <div className="h-full flex flex-col justify-between p-6">
        <TextLargeParagraph
          label={title}
          color="text-ds-natural-600"
        />
        {isLoading ? (
          <div className="bg-ds-natural-150 rounded-sm h-2 w-1/2 mt-0.5 animate-pulse" />
        ) : (
          <TextCaption label={breakdown} />
        )}
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            {isLoading ? (
              <div className="bg-ds-natural-150 rounded-sm h-4 animate-pulse mt-4 w-10" />
            ) : (
              roundedTotal && (
                <div className="flex items-end">
                  <TooltipBubble
                    placement="top"
                    title={String(total)}
                  >
                    <TextPageHeader
                      label={String(roundedTotal)}
                      color="text-ds-natural-600"
                    />
                  </TooltipBubble>
                </div>
              )
            )}
          </div>
          <div className="w-4/5">
            {isLoading ? (
              <KpiLineChartSkeleton />
            ) : (
              chart && (
                <LineChart
                  options={{
                    ...minimalChartOptions,
                    xAxis: {
                      ...minimalChartOptions.xAxis,
                      categories: chart.timestamps,
                    },
                    chart: {
                      type: 'spline',
                      height: 56,
                      margin: 10,
                    },
                    tooltip: baseTooltipOptions,
                    series: [
                      {
                        name: title,
                        color: chartColor,
                        marker: { enabled: false },
                        data: chart.values,
                        type: 'spline',
                      },
                    ],
                  }}
                />
              )
            )}
          </div>
        </div>
        {isLoading ? (
          <div className="bg-ds-natural-150 rounded-sm h-2 animate-pulse w-2/3 mt-4 flex items-center" />
        ) : (
          <TrendLabel trend={trend} />
        )}
      </div>
    </Paper>
  );
};
